import * as Sentry from '@sentry/react';

/**
 * Global error boundary fallback component with Sentry integration
 * This component is displayed when a critical application error occurs
 *
 * @param {Object} props - Component props
 * @param {Error} [props.error] - The error that occurred
 * @param {string} [props.eventId] - Sentry event ID for the error
 * @returns {JSX.Element} Global error fallback UI
 */
const GlobalErrorFallback = ({ error, eventId }) => {
  /**
   * Open Sentry feedback dialog
   */
  const openSentryFeedback = () => {
    if (eventId) {
      Sentry.showReportDialog({
        eventId,
        user: {
          email: '', // Pre-fill if user email is available
          name: '', // Pre-fill if user name is available
        },
        title: "It looks like we're having issues.",
        subtitle: 'Our team has been notified.',
        subtitle2: "If you'd like to help, tell us what happened below.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <div className="text-center">
          {/* Error Icon */}
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-red-100 p-4">
              <svg
                className="h-12 w-12 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>

          {/* Error Title */}
          <h1 className="text-2xl font-bold text-red-900 mb-2">
            Critical Application Error
          </h1>

          {/* Error Description */}
          <p className="text-red-700 mb-6">
            The application failed to load. Please refresh the page or contact
            support if the problem persists.
          </p>

          {/* Error Details (Development only) */}
          {import.meta.env.MODE === 'development' && error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-left">
              <h3 className="text-sm font-medium text-red-800 mb-2">
                Error Details:
              </h3>
              <div className="text-xs text-red-700 font-mono break-all">
                <p className="mb-2">
                  <strong>Message:</strong> {error.message}
                </p>
                {eventId && (
                  <p className="mb-2">
                    <strong>Event ID:</strong> {eventId}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Sentry Event ID (Production) */}
          {import.meta.env.MODE !== 'development' && eventId && (
            <p className="text-xs text-gray-500 mb-6">
              This error has been automatically reported to our team.
              <br />
              Error ID: <span className="font-mono">{eventId}</span>
            </p>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium shadow-sm"
            >
              Reload Application
            </button>

            {eventId && (
              <button
                onClick={openSentryFeedback}
                className="w-full px-6 py-3 bg-white text-red-600 border-2 border-red-600 rounded-md hover:bg-red-50 transition-colors font-medium"
              >
                Report Issue
              </button>
            )}

            <button
              onClick={() => (window.location.href = '/')}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium"
            >
              Go to Home
            </button>
          </div>

          {/* Support Info */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              If you continue to experience issues, please contact our support
              team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalErrorFallback;
